import React, { useState } from 'react';
import { useTranslation } from '../../contexts/TranslationContext';
import { Card, CardContent } from '../ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar';
import { Button } from '../ui/button';

const TeamSection = () => {
  const { t } = useTranslation();
  const [viewMode, setViewMode] = useState('grid');

  // Team members data from translations
  const teamMembers = [
    {
      id: 1,
      name: t('about.team.members.hachim.name'),
      role: t('about.team.members.hachim.role'),
      bio: t('about.team.members.hachim.bio'),
      // image: "https://api.dicebear.com/7.x/initials/svg?seed=Hachim", // Removed external API dependency
      expertise: ["Leadership", "Sales Strategy", "Team Management"]
    },
    {
      id: 2,
      name: t('about.team.members.hasna.name'),
      role: t('about.team.members.hasna.role'),
      bio: t('about.team.members.hasna.bio'),
      // image: "https://api.dicebear.com/7.x/initials/svg?seed=Hasna",
      expertise: ["Prospecting", "Lead Generation", "Client Acquisition"]
    },
    {
      id: 3,
      name: t('about.team.members.hicham.name'),
      role: t('about.team.members.hicham.role'),
      bio: t('about.team.members.hicham.bio'),
      // image: "https://api.dicebear.com/7.x/initials/svg?seed=Hicham",
      expertise: ["Sales", "Client Relations", "Business Development"]
    },
    {
      id: 4,
      name: t('about.team.members.saad.name'),
      role: t('about.team.members.saad.role'),
      bio: t('about.team.members.saad.bio'),
      // image: "https://api.dicebear.com/7.x/initials/svg?seed=Saad",
      expertise: ["Sales", "Market Analysis", "Customer Engagement"]
    },
    {
      id: 5,
      name: t('about.team.members.hajar.name'),
      role: t('about.team.members.hajar.role'),
      bio: t('about.team.members.hajar.bio'),
      // image: "https://api.dicebear.com/7.x/initials/svg?seed=Hajar",
      expertise: ["Sales", "Customer Service", "Client Experience"]
    },
    {
      id: 6,
      name: t('about.team.members.salma.name'),
      role: t('about.team.members.salma.role'),
      bio: t('about.team.members.salma.bio'),
      // image: "https://api.dicebear.com/7.x/initials/svg?seed=Salma",
      expertise: ["Sales", "Client Relations", "Business Growth"]
    }
  ];

  return (
    <section className="py-24 bg-background">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
            {t('about.team.title')}
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto mb-8">
            {t('about.team.quotes')}
          </p>
          
          {/* View Mode Toggle */}
          <div className="flex justify-center space-x-2">
            <Button
              variant={viewMode === 'grid' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setViewMode('grid')}
              className="px-4"
            >
              {t('about.team.view.grid')}
            </Button>
            <Button
              variant={viewMode === 'orbit' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setViewMode('orbit')}
              className="px-4"
            >
              {t('about.team.view.orbit')}
            </Button>
          </div>
        </div>

        {/* Team Members */}
        {viewMode === 'grid' ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {teamMembers.map((member) => (
              <Card key={member.id} className="group hover:shadow-lg transition-all duration-300 border-border/50 hover:border-primary/20">
                <CardContent className="p-6 text-center">
                  <div className="relative mb-4">
                    <Avatar className="w-24 h-24 mx-auto">
                      <AvatarImage src={member.image} alt={member.name} />
                      <AvatarFallback>
                        {member.name.split(' ').map(n => n[0]).join('')}
                      </AvatarFallback>
                    </Avatar>
                    <div className="absolute -bottom-2 -right-2 w-6 h-6 bg-primary rounded-full flex items-center justify-center">
                      <span className="text-white text-xs">✓</span>
                    </div>
                  </div>
                  
                  <h3 className="text-xl font-bold text-foreground mb-1">
                    {member.name}
                  </h3>
                  <p className="text-primary font-medium mb-3">
                    {member.role}
                  </p>
                  <p className="text-muted-foreground text-sm mb-4 leading-relaxed">
                    {member.bio}
                  </p>
                  
                  {/* Expertise Tags */}
                  <div className="flex flex-wrap justify-center gap-2">
                    {member.expertise.map((skill, index) => (
                      <span
                        key={index}
                        className="px-2 py-1 bg-muted text-muted-foreground text-xs rounded-full"
                      >
                        {skill}
                      </span>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          /* Orbit View - Circular Layout */
          <div className="relative w-full h-96 flex items-center justify-center">
            <div className="relative w-80 h-80">
              {teamMembers.map((member, index) => {
                const angle = (index * 360) / teamMembers.length;
                const radius = 140;
                const x = Math.cos((angle * Math.PI) / 180) * radius;
                const y = Math.sin((angle * Math.PI) / 180) * radius;
                
                return (
                  <div
                    key={member.id}
                    className="absolute transform -translate-x-1/2 -translate-y-1/2 group cursor-pointer"
                    style={{
                      left: `calc(50% + ${x}px)`,
                      top: `calc(50% + ${y}px)`,
                    }}
                  >
                    <div className="relative">
                      <Avatar className="w-16 h-16 border-4 border-background shadow-lg group-hover:scale-110 transition-transform duration-300">
                        <AvatarImage src={member.image} alt={member.name} />
                        <AvatarFallback>
                          {member.name.split(' ').map(n => n[0]).join('')}
                        </AvatarFallback>
                      </Avatar>
                      
                      {/* Tooltip */}
                      <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none">
                        <div className="bg-background border border-border rounded-lg p-3 shadow-lg min-w-48">
                          <h4 className="font-bold text-foreground text-sm">{member.name}</h4>
                          <p className="text-primary text-xs">{member.role}</p>
                          <p className="text-muted-foreground text-xs mt-1">{member.bio}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
              
              {/* Center Logo/Text */}
              <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 text-center">
                <div className="w-20 h-20 bg-primary rounded-full flex items-center justify-center mx-auto mb-2">
                  <span className="text-white font-bold text-lg">E</span>
                </div>
                <p className="text-sm text-muted-foreground font-medium">Envael Team</p>
              </div>
            </div>
          </div>
        )}

        {/* Call to Action */}
        <div className="text-center mt-16">
          <p className="text-muted-foreground mb-6">
            {t('about.team.joinUs')}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button className="px-8">
              {t('about.cta.primary')}
            </Button>
            <Button variant="outline" className="px-8">
              {t('about.cta.secondary')}
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default TeamSection;
